import React from 'react';
import classNames from 'classnames';
import SelectBase, { createFilter } from 'react-select';
import { emphasize, styled, useTheme } from '@mui/material/styles';
import { FormControl } from '@mui/material';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import {inputHeight, inputFontSize} from "../theme";
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import CancelIcon from '@mui/icons-material/Cancel';
import { FixedSizeList } from "react-window";
import isEqual from 'lodash.isequal';

// react-select's strict generics reject this component's dynamic/custom props
// (e.g. textFieldProps); treat the element as untyped, matching EnhancedSelect.tsx.
const Select: any = SelectBase;

const StyledChip = styled(Chip)(({ theme }) => ({
  '&.chipFocused': {
    backgroundColor: emphasize(
      theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  }
}));

const NoOptionsTypography = styled(Typography)(({ theme }) => ({
  padding: `${theme.spacing()}px ${theme.spacing()}px`,
}));

const PlaceholderTypography = styled(Typography)({
  fontSize: 16,
});

const SingleValueTypography = styled(Typography)({
  fontSize: inputFontSize,
});

const StyledPaper = styled(Paper)({
  position: 'absolute',
  zIndex: 10,
  left: 0,
  right: 0
});

const ValueContainerDiv = styled('div')<any>(({ theme, isMulti, variant }) => ({
  display: 'flex',
  flex: '1 1 auto',
  alignItems: 'center',
  ...(variant === 'outlined' || variant === 'filled' ? { paddingLeft: '16px' } : {}),
  ...(isMulti ? {
    flexWrap: 'wrap',
    maxHeight: '100px',
    overflow: 'auto',
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(0.5)
  } : {
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxHeight: '100px',
    overflow: 'hidden',
  })
}));

function NoOptionsMessage(props: any) {
  return (
    <NoOptionsTypography
      color="textSecondary"
      {...props.innerProps}
    >
      {props.children}
    </NoOptionsTypography>
  );
}

const inputComponent = React.forwardRef<any, any>(({ inputRef, style, ...props }, ref) => {
  return <div ref={inputRef || ref} {...props} style={{ display: 'flex', ...style }} />;
});

function Control(variant: any, classes: any, props: any) {
  return (
    <TextField
      fullWidth
      variant={variant}
      {...props.selectProps.textFieldProps}
      slotProps={{
        input: {
          inputComponent,
          inputProps: {
            className: classes.input,
            inputRef: props.innerRef,
            children: props.children,
            ...props.innerProps,
          },
          slotProps: {
            notchedOutline: {
              style: {
                position: 'absolute',
                bottom: 0,
                right: 0,
                top: '-5px',
                left: 0,
                margin: 0,
                padding: '0 8px',
                pointerEvents: 'none',
                overflow: 'hidden',
              }
            }
          },
          style: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            minHeight: inputHeight,
            flexWrap: 'wrap',
          },
          sx: {
            minHeight: inputHeight,
            alignItems: 'center',
            '& .MuiInputBase-input': {
              height: 'auto !important',
              padding: 0
            }
          }
        }
      } as any} />
  );
}

function Option(props: any) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
        height: '45px',
        backgroundColor: props.isFocused ? '#f5f5f5' : 'transparent',
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props: any) {
  return (
    <PlaceholderTypography
      color="textSecondary"
      {...props.innerProps}
    >
      {props.children}
    </PlaceholderTypography>
  );
}

function SingleValue(props: any) {
  return (
    <SingleValueTypography {...props.innerProps}>
      {props.children}
    </SingleValueTypography>
  );
}

function ValueContainer(variant: any, props: any) {
  return <ValueContainerDiv isMulti={props.isMulti} variant={variant}>{props.children}</ValueContainerDiv>;
}

function MultiValue(props: any) {
  return (
    <StyledChip
      tabIndex={-1}
      label={props.children}
      className={classNames({
        chipFocused: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props: any) {
  return (
    <StyledPaper square {...props.innerProps}>
      {props.children}
    </StyledPaper>
  );
}

const height = 45;

function MenuList(props: any) {
  const { options, children, maxHeight, getValue } = props;
  const [value] = getValue();
  var optionValue = value ? options.find(r => r.value === value.value) : value;
  var initialOffset = options.indexOf(optionValue) * height;
  var items = children && children.length ? children : [];
  var menuHeight = maxHeight;
  if (menuHeight > items.length * height) {
    menuHeight = (items.length * height) + 1;
  }
  if (menuHeight > initialOffset) {
    initialOffset = 0;
  }
  if (menuHeight < height) {
    menuHeight = 0;
  }
  return (
    <FixedSizeList
      height={menuHeight}
      itemCount={items.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{items[index]}</div>}
    </FixedSizeList>
  );
}

const defaultComponents = {
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  MenuList
};

const filterOption = createFilter({ ignoreAccents: false });

interface ExtendedSelectOption {
  label: string;
  value: any;
  [key: string]: any;
}

interface ExtendedSelectProps {
  input?: {
    value?: any;
    onChange?: (value: any) => void;
    [key: string]: any;
  };
  options?: ExtendedSelectOption[];
  value?: any;
  onChange?: (value: any) => void;
  variant?: any;
  allowSelectAll?: boolean;
  controlled?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  label?: React.ReactNode;
  style?: React.CSSProperties;
  meta?: any;
  theme?: any;
  [key: string]: any;
}

interface ExtendedSelectState {
  value: any;
  single?: any;
}

interface ExtendedSelectHandle {
  setValue: (value: any) => void;
}

const ExtendedSelect = React.forwardRef<ExtendedSelectHandle, ExtendedSelectProps>((props, ref) => {
  // `options` is optional on the public prop type; default to an empty array so
  // the many .find/.filter dereferences below can never throw when a consumer
  // omits it (matches the non-breaking safe-default contract).
  const options = props.options ?? [];
  const [state, setState] = React.useState<ExtendedSelectState>(() => {
    const initial: ExtendedSelectState = { value: null };
    if (props.input) {
      initial.single = options.find(r => r.value === props.input.value);
    }
    // Initialize value in constructor to avoid null→value flash
    if (!props.input && options && props.value != null) {
      if (Array.isArray(props.value)) {
        initial.value = options.filter(r => props.value.find(x => x === r.value));
      } else {
        initial.value = options.find(r => r.value === props.value);
      }
    }
    return initial;
  });

  // Instance-level components to prevent cross-instance overwrites
  const components = React.useMemo(() => {
    const classes = {
      input: 'select-input'
    };
    return {
      ...defaultComponents,
      Control: Control.bind(null, props.variant, classes),
      ValueContainer: ValueContainer.bind(null, props.variant),
    };
  }, [props.variant]);

  const optionsRef = React.useRef<any[] | undefined>(undefined);

  const getOnChange = () => {
    if (props.onChange) {
      return props.onChange;
    }
    if (props.input) {
      return props.input.onChange;
    }
    return null;
  };

  const setValue = (value: any) => {
    if (value || value === 0) {
      setState(prev => ({ ...prev, value: options.find(r => r.value === value) }));
    } else {
      setState(prev => ({ ...prev, value: value }));
    }
  };

  const handleChange = (value: any) => {
    if (!value) {
      setState(prev => ({ ...prev, value: null }));
      let onChange = getOnChange();
      if (onChange) { onChange(null); }
      return;
    }
    if (props.allowSelectAll && (value.value === 'selectAllOptions' || (Array.isArray(value) && value.find(r => r.value === 'selectAllOptions')))) {
      value = options;
    }
    setState(prev => ({ ...prev, value: value }));
    let onChange = getOnChange();
    if (onChange) {
      if (Array.isArray(props.value)) {
        onChange(value.map(r => r.value));
      } else {
        onChange((value || {}).value);
      }
    }
  };

  const setUpOptions = (opts: any) => {
    var selectAll = [{ label: "Select All", value: 'selectAllOptions' }];
    if (props.allowSelectAll) {
      opts = selectAll.concat(opts);
    }
    return opts;
  };

  React.useImperativeHandle(ref, () => ({ setValue }));

  // componentDidMount
  React.useEffect(() => {
    if (!props.input) {
      if (Array.isArray(props.value)) {
        setState(prev => ({ ...prev, value: options.filter(r => props.value.find(x => x === r.value)) }));
      } else {
        setState(prev => ({ ...prev, value: options.find(r => r.value === props.value) }));
      }
    }
    optionsRef.current = options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate (runs after every update, but not on mount)
  const didMountRef = React.useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (options && !isEqual(options, optionsRef.current)) {
      optionsRef.current = options;
    }
    if (props.controlled && options) {
      if (Array.isArray(props.value)) {
        let validValues = options.filter(r => props.value.find(x => x === r.value));
        if (!isEqual(state.value, validValues)) {
          setState(prev => ({ ...prev, value: validValues }));
        }
      } else if (state.value !== options.find(r => r.value === props.value) && state.value !== props.value) {
        setValue(props.value);
      }
    }
  });

  const { meta, theme } = props;

  const selectStyles = {
    container: base => ({
      ...base,
    }),
    input: base => ({
      ...base,
      color: theme?.palette?.text?.primary,
      '& input': {
        font: 'inherit'
      },
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 1400
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '6px',
      cursor: 'pointer'
    }),
    clearIndicator: base => ({
      ...base,
      padding: '6px',
      cursor: 'pointer'
    })
  };

  let showError = !(!(meta && (meta.submitError || meta.error)));

  return (
    <FormControl style={{ ...props.style }}>
      <Select
        menuPortalTarget={document.body}
        textFieldProps={{
          label: props.label,
          InputLabelProps: {
            shrink: true,
          },
          error: showError
        }}
        styles={selectStyles}
        filterOption={filterOption}
        options={setUpOptions(options)}
        components={components}
        value={state.value}
        isDisabled={props.disabled}
        isLoading={props.isLoading}
        onChange={handleChange}
        placeholder={props.placeholder}
        isSearchable={true}
        isMulti={Array.isArray(props.value)}
        isClearable={props.isClearable}
      />
      {showError && ((meta.error && meta.error !== true) || (meta.submitError && meta.submitError !== true)) &&
        <FormHelperText error={true}>{meta.error || meta.submitError}</FormHelperText>
      }
    </FormControl>
  );
});

const ExtendedSelectWithTheme = React.forwardRef<any, ExtendedSelectProps & {innerRef?: any}>(({ innerRef, ...props }, ref) => {
  const theme = useTheme();
  return <ExtendedSelect {...props} ref={innerRef || ref} theme={theme} />;
});

export default ExtendedSelectWithTheme;
